import { clearReviewActivitySession } from './review-activity-session';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import {
    useQuery,
    useMutation,
    queryOptions,
    useQueryClient,
} from '@tanstack/react-query';
import { CurrentUser, User } from 'types/api';
import { datadogRum } from '@datadog/browser-rum';

import { api } from './api-client';
import { QueryConfig } from './react-query';

// api call definitions for auth (types, schemas, requests):
// these are not part of features as this is a module shared across features

type GetUserResponse = {
    user: User;
    deployment_url: string | null;
};

const getUser = async (): Promise<CurrentUser> => {
    const response = await api.get<GetUserResponse, GetUserResponse>(
        '/app/api/v1/user',
    );
    return {
        ...response.user,
        deployment_url: response.deployment_url,
    };
};

export const getUserQueryOptions = () =>
    queryOptions({
        queryKey: ['user'],
        queryFn: getUser,
    });

const logout = async (): Promise<void> => {
    const { discardLocalDocumentSession } = await import(
        './local-document-session'
    );
    clearReviewActivitySession();
    await discardLocalDocumentSession();
    return api.post('/app/api/v1/logout');
};

export const synchronizeAuthenticatedSession = async (
    organizationSwitchIntent?: string,
    requireOrganizationSwitchIntent = false,
): Promise<void> => {
    const { discardLocalDocumentSession } = await import(
        './local-document-session'
    );
    clearReviewActivitySession();
    await discardLocalDocumentSession();
    await api.post(
        '/public/api/v1/auth',
        organizationSwitchIntent || requireOrganizationSwitchIntent
            ? {
                  organization_switch_intent: organizationSwitchIntent,
                  require_organization_switch_intent:
                      requireOrganizationSwitchIntent,
              }
            : undefined,
    );
};

export type SessionOrganization = {
    stytch_organization_id: string;
    name: string;
};

export type SessionOrganizationsResponse = {
    current_stytch_organization_id: string;
    organizations: SessionOrganization[];
};

export const createOrganizationSwitchIntent = async (
    stytchOrganizationId: string,
): Promise<string> => {
    const response = await api.post<
        { switch_intent: string },
        { switch_intent: string }
    >('/public/api/v1/session/organization-switch-intents', {
        target_stytch_organization_id: stytchOrganizationId,
    });
    return response.switch_intent;
};

export const getSessionOrganizations =
    async (): Promise<SessionOrganizationsResponse> => {
        return api.get<
            SessionOrganizationsResponse,
            SessionOrganizationsResponse
        >('/public/api/v1/session/organizations');
    };

export const getSessionOrganizationsQueryOptions = () =>
    queryOptions({
        queryKey: ['session', 'organizations'],
        queryFn: getSessionOrganizations,
        staleTime: 0,
    });

export const useSessionOrganizations = ({ enabled = false } = {}) =>
    useQuery({
        ...getSessionOrganizationsQueryOptions(),
        enabled,
    });

type UseUserOptions = {
    queryConfig?: QueryConfig<typeof getUserQueryOptions>;
};

export const useUser = ({ queryConfig }: UseUserOptions = {}) => {
    const query = useQuery({
        ...getUserQueryOptions(),
        ...queryConfig,
    });
    const { data } = query;

    if (data) {
        datadogRum.setUser({
            id: data.id,
            email: data.email,
            name: data.name,
        });
    }

    return query;
};

export const useAuthenticate = (
    onSuccess: () => void,
    onError: (error: Error) => void,
    organizationSwitchIntent?: string,
    requireOrganizationSwitchIntent = false,
) => {
    const queryClient = useQueryClient();
    const mutation = useMutation({
        onSuccess: () => {
            queryClient.clear();
            onSuccess();
        },
        onError: onError,
        mutationFn: () =>
            synchronizeAuthenticatedSession(
                organizationSwitchIntent,
                requireOrganizationSwitchIntent,
            ),
    });
    return mutation;
};

export const useLogout = (onSuccess: () => void) => {
    const mutation = useMutation({
        mutationFn: logout,
        onSuccess: onSuccess,
    });
    return mutation;
};

export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const location = useLocation();
    const { session } = useStytchMemberSession();
    useEffect(() => {
        if (!session)
            void import('./local-document-session').then(
                ({ discardLocalDocumentSession }) =>
                    discardLocalDocumentSession(),
            );
    }, [session]);

    if (!session) {
        return (
            <Navigate
                to={`/authenticate?redirectTo=${encodeURIComponent(location.pathname)}`}
                replace
            />
        );
    }

    return children;
};
